import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "./ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Plus, Trash2, ShieldOff, ShieldCheck, Pencil, KeyRound } from "lucide-react";

// Admin- and Super-Admin-facing employee/admin account manager.
// Backend enforces the actual RBAC & branch scope; this UI just wires the flows.
export default function EmployeeManagement({ currentUser, onChanged }) {
    const isSuper = currentUser?.role === "super_admin";
    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [openCreate, setOpenCreate] = useState(false);
    const [form, setForm] = useState({
        username: "",
        password: "",
        name: "",
        role: "employee",
        branch_ids: [],
    });

    const [pwDlg, setPwDlg] = useState(null);       // reset password target
    const [pwNew, setPwNew] = useState("");
    const [editDlg, setEditDlg] = useState(null);   // edit-name target
    const [editForm, setEditForm] = useState({ name: "", branch_ids: [] });
    const [disableDlg, setDisableDlg] = useState(null); // disable target
    const [disableForm, setDisableForm] = useState({ password: "", reason: "" });
    const [deleteDlg, setDeleteDlg] = useState(null);   // permanent delete target
    const [deleteForm, setDeleteForm] = useState({ password: "", reason: "" });
    const [busy, setBusy] = useState(false);

    const branchLabel = useMemo(() => {
        const m = new Map(branches.map((b) => [b.id, b.code]));
        return (id) => m.get(id) || id.slice(0, 6);
    }, [branches]);

    async function load() {
        const [u, b] = await Promise.all([
            api.get("/users"),
            api.get("/branches").catch(() => ({ data: [] })),
        ]);
        setUsers(u.data || []);
        setBranches(b.data || []);
    }
    useEffect(() => { load(); }, []);

    // Branches this operator is allowed to assign.
    const assignableBranches = useMemo(() => {
        if (isSuper) return branches;
        const mine = new Set(currentUser?.branch_ids || []);
        return branches.filter((b) => mine.has(b.id));
    }, [branches, isSuper, currentUser]);

    function toggleBranch(list, id) {
        return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    }

    async function submitCreate() {
        if (!form.username.trim() || !form.password) {
            return toast.error("Username and temporary password are required");
        }
        if (form.password.length < 6) {
            return toast.error("Temporary password must be at least 6 characters");
        }
        setBusy(true);
        try {
            await api.post("/users", {
                ...form,
                username: form.username.trim().toLowerCase(),
            });
            toast.success(`Created ${form.name || form.username}. They must change the temporary password on first login.`);
            setForm({ username: "", password: "", name: "", role: "employee", branch_ids: [] });
            setOpenCreate(false);
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function submitResetPw() {
        if (!pwDlg) return;
        if (!pwNew || pwNew.length < 6) return toast.error("Password must be at least 6 characters");
        setBusy(true);
        try {
            const { data } = await api.post(`/users/${pwDlg.id}/reset-password`, {
                new_password: pwNew,
            });
            toast.success(`Password reset for ${data.username}. They must change it on next login.`);
            setPwDlg(null);
            setPwNew("");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function submitEdit() {
        if (!editDlg) return;
        if (!editForm.name.trim()) return toast.error("Name cannot be empty");
        setBusy(true);
        try {
            await api.patch(`/users/${editDlg.id}`, {
                name: editForm.name.trim(),
                branch_ids: editForm.branch_ids,
            });
            toast.success("Updated");
            setEditDlg(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function submitDisable() {
        if (!disableDlg) return;
        if (!disableForm.password) return toast.error("Confirm your password");
        if (!disableForm.reason.trim()) return toast.error("Provide a reason");
        setBusy(true);
        try {
            await api.post(`/users/${disableDlg.id}/disable`, {
                password_confirm: disableForm.password,
                reason: disableForm.reason.trim(),
            });
            toast.success("Account disabled");
            setDisableDlg(null);
            setDisableForm({ password: "", reason: "" });
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function toggleEnable(u) {
        setBusy(true);
        try {
            await api.post(`/users/${u.id}/enable`);
            toast.success("Account re-enabled");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function submitDelete() {
        if (!deleteDlg) return;
        if (!deleteForm.password) return toast.error("Confirm your password");
        if (!deleteForm.reason.trim()) return toast.error("Provide a reason");
        setBusy(true);
        try {
            await api.request({
                method: "delete",
                url: `/users/${deleteDlg.id}`,
                data: {
                    password_confirm: deleteForm.password,
                    reason: deleteForm.reason.trim(),
                },
            });
            toast.success("Account permanently deleted");
            setDeleteDlg(null);
            setDeleteForm({ password: "", reason: "" });
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Card className="rounded-2xl border-slate-100 p-5" data-testid="employee-mgmt-card">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="font-display font-bold text-slate-900">
                        {isSuper ? "Admin & Employee Logins" : "Employee Logins"}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {isSuper
                            ? "You can manage Admin and Employee accounts across every branch."
                            : "You can only see and manage Employees in your assigned branch(es)."}
                    </p>
                </div>
                <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                    <DialogTrigger asChild>
                        <Button
                            data-testid="emp-mgmt-add-btn"
                            className="h-10 rounded-xl bg-blue-900 hover:bg-blue-800"
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            {isSuper ? "Add Login" : "Add Employee"}
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle>
                                {isSuper ? "Create Admin or Employee login" : "Create Employee login"}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label>Full name</Label>
                                <Input
                                    data-testid="emp-mgmt-name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="h-11 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label>Username</Label>
                                <Input
                                    data-testid="emp-mgmt-username"
                                    value={form.username}
                                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                                    className="h-11 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label>Temporary password</Label>
                                <Input
                                    type="password"
                                    data-testid="emp-mgmt-temp-pw"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="h-11 rounded-xl"
                                />
                                <p className="text-[11px] text-slate-500 mt-1">
                                    The user will be forced to change this on first login.
                                </p>
                            </div>
                            {isSuper && (
                                <div>
                                    <Label>Role</Label>
                                    <Select
                                        value={form.role}
                                        onValueChange={(v) => setForm({ ...form, role: v })}
                                    >
                                        <SelectTrigger className="h-11 rounded-xl" data-testid="emp-mgmt-role">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="employee">Employee</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div>
                                <Label>Branches</Label>
                                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="emp-mgmt-branches">
                                    {assignableBranches.length === 0 ? (
                                        <span className="text-xs text-slate-500">
                                            No branches available.
                                        </span>
                                    ) : (
                                        assignableBranches.map((b) => {
                                            const on = form.branch_ids.includes(b.id);
                                            return (
                                                <button
                                                    key={b.id}
                                                    type="button"
                                                    onClick={() => setForm({ ...form, branch_ids: toggleBranch(form.branch_ids, b.id) })}
                                                    data-testid={`emp-mgmt-branch-${b.code}`}
                                                    className={`px-3 h-8 rounded-full text-xs font-semibold border ${
                                                        on
                                                            ? "bg-blue-900 text-white border-blue-900"
                                                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                                    }`}
                                                >
                                                    {b.code} · {b.name}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                                {!isSuper && (
                                    <p className="text-[11px] text-slate-500 mt-1">
                                        Leaving this empty will assign the employee to all of your branch(es).
                                    </p>
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                onClick={submitCreate}
                                disabled={busy}
                                data-testid="emp-mgmt-create-confirm"
                                className="bg-blue-900 hover:bg-blue-800"
                            >
                                Create login
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="divide-y divide-slate-100">
                {users.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400">No accounts to manage.</div>
                ) : users.map((u) => (
                    <div
                        key={u.id}
                        className="flex items-center justify-between gap-3 py-3"
                        data-testid={`emp-mgmt-row-${u.id}`}
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <div className="font-semibold text-slate-900 truncate">{u.name}</div>
                                <Badge
                                    className={
                                        u.role === "admin" || u.role === "super_admin"
                                            ? "bg-blue-50 text-blue-900 border-blue-100 hover:bg-blue-50"
                                            : "bg-slate-100 text-slate-700 hover:bg-slate-100"
                                    }
                                >
                                    {u.role}
                                </Badge>
                                {u.disabled && (
                                    <Badge className="bg-rose-50 text-rose-800 border-rose-200">
                                        Disabled
                                    </Badge>
                                )}
                                {u.must_change && !u.disabled && (
                                    <Badge className="bg-amber-50 text-amber-800 border-amber-200">
                                        Temp password
                                    </Badge>
                                )}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                                @{u.username}
                                {(u.branch_ids || []).length > 0 && (
                                    <> · <span className="text-slate-600">{u.branch_ids.map(branchLabel).join(", ")}</span></>
                                )}
                            </div>
                        </div>
                        {u.id !== currentUser?.id && (
                            <div className="flex items-center gap-1 shrink-0">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    title="Edit"
                                    data-testid={`emp-mgmt-edit-${u.id}`}
                                    onClick={() => {
                                        setEditDlg(u);
                                        setEditForm({
                                            name: u.name || "",
                                            branch_ids: u.branch_ids || [],
                                        });
                                    }}
                                    className="h-9 w-9 p-0"
                                >
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    title="Reset password"
                                    data-testid={`emp-mgmt-reset-${u.id}`}
                                    onClick={() => { setPwDlg(u); setPwNew(""); }}
                                    className="h-9 w-9 p-0"
                                >
                                    <KeyRound className="h-4 w-4" />
                                </Button>
                                {u.disabled ? (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        title="Enable"
                                        data-testid={`emp-mgmt-enable-${u.id}`}
                                        onClick={() => toggleEnable(u)}
                                        disabled={busy}
                                        className="h-9 w-9 p-0 text-emerald-700"
                                    >
                                        <ShieldCheck className="h-4 w-4" />
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        title="Disable"
                                        data-testid={`emp-mgmt-disable-${u.id}`}
                                        onClick={() => { setDisableDlg(u); setDisableForm({ password: "", reason: "" }); }}
                                        className="h-9 w-9 p-0 text-amber-700"
                                    >
                                        <ShieldOff className="h-4 w-4" />
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    title="Permanent delete"
                                    data-testid={`emp-mgmt-delete-${u.id}`}
                                    onClick={() => { setDeleteDlg(u); setDeleteForm({ password: "", reason: "" }); }}
                                    className="h-9 w-9 p-0 text-rose-600 hover:bg-rose-50"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Reset password dialog */}
            <Dialog open={!!pwDlg} onOpenChange={(o) => !o && setPwDlg(null)}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Reset password — {pwDlg?.name}</DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-slate-500">
                        Sets a new temporary password. The user will be forced to change it on next login.
                    </p>
                    <div>
                        <Label>New temporary password (min 6 chars)</Label>
                        <Input
                            type="password"
                            data-testid="emp-mgmt-reset-input"
                            value={pwNew}
                            onChange={(e) => setPwNew(e.target.value)}
                            className="h-11 rounded-xl mt-1.5"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={submitResetPw}
                            disabled={busy}
                            data-testid="emp-mgmt-reset-confirm"
                            className="bg-blue-900 hover:bg-blue-800"
                        >
                            Reset password
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={!!editDlg} onOpenChange={(o) => !o && setEditDlg(null)}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit login — @{editDlg?.username}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Full name</Label>
                            <Input
                                data-testid="emp-mgmt-edit-name"
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="h-11 rounded-xl"
                            />
                        </div>
                        <div>
                            <Label>Branches</Label>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {assignableBranches.map((b) => {
                                    const on = editForm.branch_ids.includes(b.id);
                                    return (
                                        <button
                                            key={b.id}
                                            type="button"
                                            onClick={() => setEditForm({ ...editForm, branch_ids: toggleBranch(editForm.branch_ids, b.id) })}
                                            data-testid={`emp-mgmt-edit-branch-${b.code}`}
                                            className={`px-3 h-8 rounded-full text-xs font-semibold border ${
                                                on
                                                    ? "bg-blue-900 text-white border-blue-900"
                                                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                            }`}
                                        >
                                            {b.code} · {b.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={submitEdit}
                            disabled={busy}
                            data-testid="emp-mgmt-edit-confirm"
                            className="bg-blue-900 hover:bg-blue-800"
                        >
                            Save changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Disable dialog */}
            <Dialog open={!!disableDlg} onOpenChange={(o) => !o && setDisableDlg(null)}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Disable @{disableDlg?.username}</DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-slate-500">
                        Blocks this user from logging in. Requires your current password and a reason for the audit log.
                    </p>
                    <div className="space-y-3">
                        <div>
                            <Label>Your password</Label>
                            <Input
                                type="password"
                                data-testid="emp-mgmt-disable-password"
                                value={disableForm.password}
                                onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })}
                                className="h-11 rounded-xl"
                            />
                        </div>
                        <div>
                            <Label>Reason</Label>
                            <Input
                                data-testid="emp-mgmt-disable-reason"
                                value={disableForm.reason}
                                onChange={(e) => setDisableForm({ ...disableForm, reason: e.target.value })}
                                className="h-11 rounded-xl"
                                placeholder="e.g. left the company"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={submitDisable}
                            disabled={busy}
                            data-testid="emp-mgmt-disable-confirm"
                            className="bg-amber-600 hover:bg-amber-700"
                        >
                            Disable account
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Permanent delete dialog */}
            <Dialog open={!!deleteDlg} onOpenChange={(o) => !o && setDeleteDlg(null)}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Permanently delete @{deleteDlg?.username}</DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg p-2">
                        This is irreversible. Historical audit entries are preserved.
                    </p>
                    <div className="space-y-3">
                        <div>
                            <Label>Your password</Label>
                            <Input
                                type="password"
                                data-testid="emp-mgmt-delete-password"
                                value={deleteForm.password}
                                onChange={(e) => setDeleteForm({ ...deleteForm, password: e.target.value })}
                                className="h-11 rounded-xl"
                            />
                        </div>
                        <div>
                            <Label>Reason</Label>
                            <Input
                                data-testid="emp-mgmt-delete-reason"
                                value={deleteForm.reason}
                                onChange={(e) => setDeleteForm({ ...deleteForm, reason: e.target.value })}
                                className="h-11 rounded-xl"
                                placeholder="Reason for permanent deletion"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={submitDelete}
                            disabled={busy}
                            data-testid="emp-mgmt-delete-confirm"
                            className="bg-rose-600 hover:bg-rose-700"
                        >
                            Delete permanently
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
